/**
 * `chat.ask` (P2 design §4.6): one intent per chat message, whose `intent_id` is the chat
 * turn's id (lib/chatTurn.js; a fresh id when the turn has none). It follows the turn: the
 * first thing shown marks `first_feedback_ms`, and the turn's end — the result on screen, or a
 * newer message superseding it — ends the intent. A message that becomes a forecast also
 * opens `forecast.submit` with the same `msg_id`; "questions asked" counts `chat.ask` only.
 */
import { beginIntent } from "./instrument";

/** A turn outcome (lib/chatTurn.js vocabulary) as an intent outcome. */
export function chatAskOutcome(turnOutcome) {
  switch (turnOutcome) {
    case "superseded":
      return "superseded";
    case "error":
      return "error";
    case "aborted":
    case "ignored_busy":
    case "tos_blocked":
      return "cancelled";
    default:
      return "ok";
  }
}

/**
 * Open `chat.ask` for `turn` and follow it. `forecastId` is the active forecast's run id
 * (null in the draft); `origin` the control that sent it; `surface` the screen area it was sent
 * from: the chat unless the caller says otherwise (the new-question page's box: `ask-hero`).
 */
export function beginChatAsk(
  turn,
  { forecastId = null, origin = "chat.send", surface = "chat" } = {},
) {
  const intent = beginIntent(
    "chat.ask",
    { msg_id: turn?.msgId ?? null, forecast_id: forecastId },
    { origin, surface, id: turn?.id ?? undefined },
  );
  if (!intent.id || typeof turn?.watch !== "function") return intent;
  turn.watch({
    onMark: () => intent.mark("first_feedback_ms"),
    onEnd: (turnOutcome) => {
      const outcome = chatAskOutcome(turnOutcome);
      if (outcome === "ok") intent.mark("reply_ms");
      intent.end(outcome);
    },
  });
  return intent;
}
